import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";

const PrivacyPolicy = () => {
  document.title = "Privacy Policy | Porcher";

  return (
    <div className="page-content">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mt-4">
              <CardBody className="p-4">
                <h1 className="mb-3">Privacy Policy</h1>
                <p className="text-muted small mb-4">Last updated: August 22, 2026</p>

                <div className="content">
                  <h5 className="mt-4">1. Introduction &amp; Scope</h5>
                  <p>
                    Patronage Reservoir LLC, a Nevada limited liability company doing business as Porcher ("Porcher",
                    "we", "our", or "us"), operates the Porcher platform. This Privacy Policy explains what information
                    we collect from Homeowners, Tenants, and Applicants, how we use and share it, and the choices you
                    have. It applies to the website, the Platform's messaging and document-upload features, and any
                    related services.
                  </p>

                  <h5 className="mt-4">2. Information We Collect</h5>
                  <p>We collect the following categories of information:</p>
                  <ul>
                    <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
                    <li><strong>Property &amp; Listing Data:</strong> Property addresses, details, and photos added by Homeowners</li>
                    <li><strong>Rental Application Data:</strong> Contact info, employer, income, date of birth, and other details an Applicant submits on a rental application. Social Security numbers, when needed, are collected directly by our screening vendor and are not stored in Porcher's database</li>
                    <li><strong>Screening Results:</strong> A summary of credit, criminal, and eviction screening results provided by our third-party background-check vendor — see our{" "}
                      <Link to="/tenant-screening-policy">Tenant Screening &amp; FCRA Policy</Link>
                    </li>
                    <li><strong>Payment Data:</strong> Application fee and rent payment records processed by Stripe. Porcher does not store full card numbers or bank account numbers — those are held by Stripe</li>
                    <li><strong>Files &amp; Documents:</strong> Inspection photos, proof-of-income documents, lease documents, and other files uploaded to the Platform</li>
                    <li><strong>Messages &amp; Communications:</strong> Content you send through property chat, inspection chat, or ticket/support conversations on the Platform</li>
                    <li><strong>Usage &amp; Device Data:</strong> Pages visited, actions taken, browser type, IP address, and similar diagnostic information</li>
                  </ul>

                  <h5 className="mt-4">3. How We Use Your Information</h5>
                  <p>We use the information we collect to:</p>
                  <ul>
                    <li>Provide, maintain, and improve the Platform</li>
                    <li>Facilitate rental applications, screening requests, leases, and payments between Homeowners and Tenants/Applicants</li>
                    <li>Send administrative messages, notifications, and updates related to your account</li>
                    <li>Analyze usage patterns and troubleshoot issues</li>
                    <li>Detect, investigate, and prevent fraud, abuse, and violations of our{" "}
                      <Link to="/community-guidelines">Community &amp; Acceptable Use Policy</Link>
                    </li>
                    <li>Comply with legal obligations, including recordkeeping related to consumer reports and payments</li>
                  </ul>

                  <h5 className="mt-4">4. How We Share Information</h5>
                  <p>
                    We do not sell your personal information. We share information only as follows:
                  </p>
                  <ul>
                    <li><strong>Other users of the Platform:</strong> A Homeowner and Tenant/Applicant can see the property, application, screening summary, lease, payment, and message data relevant to their own shared property or tenancy</li>
                    <li><strong>Service providers:</strong> We use vendors to operate the Platform, including Stripe (payments), our background-check vendor (screening reports), and cloud infrastructure providers (authentication, database, and file storage). These vendors may only use your data to provide services to us</li>
                    <li><strong>Legal &amp; safety:</strong> We may disclose information if required by law, subpoena, or court order, or to protect the rights, property, or safety of Porcher, our users, or the public</li>
                    <li><strong>Business transfers:</strong> If Porcher is involved in a merger, acquisition, or asset sale, your information may be transferred as part of that transaction, subject to this Policy</li>
                  </ul>

                  <h5 className="mt-4">5. Files, Photos &amp; Documents</h5>
                  <p>
                    Files you upload (inspection photos, proof-of-income documents, lease files, and similar) are
                    stored using access-controlled storage and are only retrievable through short-lived, authenticated
                    links generated when an authorized user (you, the relevant Homeowner, or Porcher support) requests
                    them. We do not use uploaded documents for any purpose other than operating the feature you
                    uploaded them for.
                  </p>

                  <h5 className="mt-4">6. Messages &amp; In-Platform Communications</h5>
                  <p>
                    Property chat, inspection chat, and ticket conversations are retained as part of the relevant
                    property, inspection, or ticket record so participants have an accurate history and so disputes or
                    misuse can be investigated. Messages are visible to the participants in that conversation (for
                    example, a Homeowner and their tenants on a shared property) and, where necessary for support,
                    safety, or legal reasons, to authorized Porcher personnel as described below.
                  </p>

                  <h5 className="mt-4">7. Administrative &amp; Support Access</h5>
                  <p>
                    A limited number of Porcher personnel can access account, listing, application, screening-summary,
                    payment, and message data when necessary to provide customer support, investigate a reported
                    violation of our policies, maintain the security of the Platform, or comply with a legal
                    obligation. This access is restricted to what is necessary for the task at hand and is not used to
                    monitor users generally.
                  </p>

                  <h5 className="mt-4">8. Data Retention</h5>
                  <p>
                    We retain your information for as long as your account is active and for a reasonable period
                    afterward to satisfy legal, tax, audit, and dispute-resolution obligations — for example,
                    screening and payment records are typically retained longer than general account data because of
                    consumer-reporting and financial recordkeeping requirements. When information is no longer needed
                    for these purposes, we delete or anonymize it.
                  </p>

                  <h5 className="mt-4">9. Data Security</h5>
                  <p>
                    We use administrative, technical, and physical safeguards designed to protect your information,
                    including encrypted transmission, access controls, and authenticated file access. No method of
                    transmission or storage is 100% secure; if we become aware of a security incident affecting your
                    data, we will notify affected users as required by applicable law.
                  </p>

                  <h5 className="mt-4">10. Cookies &amp; Tracking</h5>
                  <p>
                    We use cookies and similar technologies to keep you signed in, remember preferences, and
                    understand how the Platform is used. You can configure your browser to refuse cookies, though some
                    features of the Platform may not function properly without them.
                  </p>

                  <h5 className="mt-4">11. Your Rights &amp; Choices</h5>
                  <p>
                    You may access, update, or request deletion of your personal information by logging into your
                    account or contacting us through the support portal. Some information — such as completed
                    screening summaries, payment records, or messages tied to another user's property — may need to be
                    retained even after a deletion request, to the extent required for legal, financial, or dispute
                    recordkeeping purposes.
                  </p>

                  <h5 className="mt-4">12. Children's Privacy</h5>
                  <p>
                    Porcher is not intended for individuals under the age of 18. We do not knowingly collect personal
                    information from children under 18. If we learn that a child under 18 has provided us with
                    personal information, we will delete it and take steps to close the associated account.
                  </p>

                  <h5 className="mt-4">13. International Users</h5>
                  <p>
                    The Platform is operated in the United States. If you access it from another country, you
                    understand your information will be transferred to and processed in the United States, where data
                    protection laws may differ from those in your country.
                  </p>

                  <h5 className="mt-4">14. Changes to This Privacy Policy</h5>
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices or for
                    other operational, legal, or regulatory reasons. Continued use of the Platform after a revised
                    Policy is posted means you accept the changes.
                  </p>

                  <h5 className="mt-4">15. Contact Us</h5>
                  <p>
                    If you have questions about this Privacy Policy or our privacy practices, please contact us
                    through the support portal in the Porcher platform.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
